import type {
  DocumentGenerationActivity,
  DocumentGenerationLogEntry,
  DocumentCareerStage,
  DocumentRewriteMode,
} from "@/types/documents";
import { documentAgentSessionRepository } from "@/cache/documentAgentSessionRepository";

export interface DocumentFormDraft {
  careerStage: DocumentCareerStage;
  coverLetterTemplate: string;
  cvFilename: string;
  cvTemplate: string;
  cvText: string;
  jobDescription: string;
  rewriteMode: DocumentRewriteMode;
}

const DRAFT_KEY_PREFIX = "applykitty.documents.form-draft";
const ACTIVITY_KEY_PREFIX = "applykitty.documents.generation-activity";
const CACHE_VERSION = 1;
const MAX_GENERATION_ACTIVITIES = 8;

interface CachedDraft extends DocumentFormDraft {
  version: number;
}

interface CachedActivities {
  activities: DocumentGenerationActivity[];
  version: number;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function key(prefix: string, userId: string): string {
  return `${prefix}:${userId}`;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isLogEntry(value: unknown): value is DocumentGenerationLogEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<DocumentGenerationLogEntry>;
  return isString(entry.stage)
    && typeof entry.progress === "number"
    && Number.isFinite(entry.progress)
    && isString(entry.message)
    && isString(entry.updatedAt);
}

function isActivity(value: unknown): value is DocumentGenerationActivity {
  if (!value || typeof value !== "object") return false;
  const activity = value as Partial<DocumentGenerationActivity>;
  return isString(activity.completedAt)
    && isString(activity.message)
    && isString(activity.packId)
    && Array.isArray(activity.logs)
    && activity.logs.every(isLogEntry);
}

export function readDocumentDraft(userId: string): DocumentFormDraft | null {
  const session = storage();
  if (!session) return null;
  const storageKey = key(DRAFT_KEY_PREFIX, userId);
  try {
    const parsed = JSON.parse(session.getItem(storageKey) ?? "null") as Partial<CachedDraft> | null;
    if (!parsed || parsed.version !== CACHE_VERSION) return null;
    const fields = [
      parsed.coverLetterTemplate,
      parsed.cvFilename,
      parsed.cvTemplate,
      parsed.cvText,
      parsed.jobDescription,
    ];
    if (fields.some((field) => !isString(field))) {
      session.removeItem(storageKey);
      return null;
    }
    return {
      coverLetterTemplate: parsed.coverLetterTemplate!,
      careerStage: parsed.careerStage === "experienced" ? "experienced" : "graduate",
      cvFilename: parsed.cvFilename!,
      cvTemplate: parsed.cvTemplate!,
      cvText: parsed.cvText!,
      jobDescription: parsed.jobDescription!,
      rewriteMode: parsed.rewriteMode === "educative" ? "educative" : "honest",
    };
  } catch {
    session.removeItem(storageKey);
    return null;
  }
}

export function writeDocumentDraft(userId: string, draft: DocumentFormDraft | null): void {
  const session = storage();
  if (!session) return;
  try {
    const storageKey = key(DRAFT_KEY_PREFIX, userId);
    if (!draft) {
      session.removeItem(storageKey);
      return;
    }
    const cached: CachedDraft = { ...draft, version: CACHE_VERSION };
    session.setItem(storageKey, JSON.stringify(cached));
  } catch {
    // The in-memory Pinia state remains usable when browser storage is unavailable.
  }
}

export function readDocumentGenerationActivities(userId: string): DocumentGenerationActivity[] {
  const session = storage();
  if (!session) return [];
  const storageKey = key(ACTIVITY_KEY_PREFIX, userId);
  try {
    const parsed = JSON.parse(session.getItem(storageKey) ?? "null") as Partial<CachedActivities> | null;
    if (!parsed || parsed.version !== CACHE_VERSION || !Array.isArray(parsed.activities)) return [];
    if (!parsed.activities.every(isActivity)) {
      session.removeItem(storageKey);
      return [];
    }
    return parsed.activities.slice(0, MAX_GENERATION_ACTIVITIES).map((activity) => ({
      ...activity,
      logs: activity.logs.map((entry) => ({ ...entry })),
    }));
  } catch {
    session.removeItem(storageKey);
    return [];
  }
}

export function writeDocumentGenerationActivities(
  userId: string,
  activities: DocumentGenerationActivity[],
): void {
  const session = storage();
  if (!session) return;
  try {
    const cached: CachedActivities = {
      activities: activities.slice(0, MAX_GENERATION_ACTIVITIES),
      version: CACHE_VERSION,
    };
    session.setItem(key(ACTIVITY_KEY_PREFIX, userId), JSON.stringify(cached));
  } catch {
    // Generation activity remains available in memory when browser storage is unavailable.
  }
}

export function clearDocumentWorkspaceCaches(): void {
  documentAgentSessionRepository.clear();
  const session = storage();
  if (!session) return;
  try {
    for (let index = session.length - 1; index >= 0; index -= 1) {
      const storageKey = session.key(index);
      if (storageKey?.startsWith(`${DRAFT_KEY_PREFIX}:`)
        || storageKey?.startsWith(`${ACTIVITY_KEY_PREFIX}:`)) {
        session.removeItem(storageKey);
      }
    }
  } catch {
    // Session reset still clears the in-memory state if storage access is restricted.
  }
}
